import { Component, EventEmitter, Input, OnInit, Output, NgZone } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';

import { AllLabels, FacilityFormType, InstallationFlowType } from '../../constants/app.constants';
import { FormTypesData, KeyValueData } from '../../domains/common.domain';
import { LocalStorageService } from '../../services/local-storage.service';
import { NavigationStateShareService } from '../../services/navigation-state-share.service';
import { JurisdictionService } from '../jurisdiction.service';
import { TechnicianDataService } from './technician-data.service';
import { ComponentFormData, LookupOptionsData } from '../jurisdiction.domain';
import {
  CreateGasGridConnectionResponse,
  CreateHeatingGridConnectionResponse,
  CreatePowerGridConnectionResponse,
  CreatePowerSpecialConsumerResponse,
  CreateWaterGridConnectionResponse,
  FormSubsectionsVisibility,
  GetGridConnectionObject,
  GridConnectionData,
  SpecialConsumerDeviceTypeChange,
  SpecialConsumerIdentity,
  UpdateChangeHouseConnectionMedien,
  UpdateGasGridConnection,
  UpdateHeatingGridConnection,
  UpdatePowerGridConnection,
  UpdatePowerSpecialConsumer,
  UpdateWaterGridConnection,
} from './technician-data.domain';
import { ErrorService } from '../../services/error/error.service';
import { CommonService } from '../../services/common.service';
import { AllDropdownKeys, UrlSegments } from '../jurisdiction.constants';
import { CommunicationService } from '../../services/communication.service';
import { EnvironmentService } from '../../services/environment.service';
import { ComponentCanDeactivate } from 'src/app/+shared/guards/pending-changes-guard.servie';
import { SectionIdentifier, SectionStatus } from 'src/app/+shared/components/review-section/review-section.domain';

@Component({
  providers: [TechnicianDataService],
  selector: 'app-technician-data',
  styleUrls: ['./technician-data.component.scss'],
  templateUrl: './technician-data.component.html',
})
export class TechnicianDataComponent implements OnInit, ComponentCanDeactivate {
  @Input() isReadOnly: boolean = false;
  @Input() isDetailsView: boolean = false;

  @Output() tempConnectionConfirmed = new EventEmitter<boolean>();
  @Output() onPowerConnectionReviewSet = new EventEmitter<SectionStatus>(); // related to https://github.com/bittner-krull/gop-web/issues/2062
  @Output() onPowerConnectionLocationChange = new EventEmitter<string>();
  @Output() specialConsumerDeviceTypeChange: EventEmitter<SpecialConsumerDeviceTypeChange> =
    new EventEmitter<SpecialConsumerDeviceTypeChange>();

  applicationId: string;
  applicationStatus: string;
  buildingTypeKey: string;
  changeSubtypeKey: string;
  currentFlowType: string;
  currentFormType: FormTypesData;
  facilityTypes: FormTypesData[];
  formSubSections: FormSubsectionsVisibility;
  infoBubblesData: {
    [key: string]: boolean;
  };
  installationFlowType: KeyValueData;
  isFormSubmit: boolean;
  isLoading: boolean;
  mediumVisibility: FormSubsectionsVisibility;
  showFormElement: boolean;
  showComponent: FormSubsectionsVisibility;
  specialDevicesCount: number;
  technicianDataFG: FormGroup;
  technicianFormTypes: FormTypesData[];
  totalSubscriptions: number;
  disabledMediaOptions: boolean;
  showDeterminePriceButton: boolean;
  baseData: any;
  externalApplicationId: string;
  gasConnectionData: any;
  heatingConnectionData: any;
  powerConnectionData: any;
  technicianData: any;
  unitLabels: KeyValueData;
  waterConnectionData: any;
  changeConnectionType: string;
  changeConnectionTypes: LookupOptionsData[];
  changeFacilities: string[];
  isPriceUndetermined: boolean;
  showLabelAndUnit: boolean;
  changePower: string;
  changeGas: string;
  changeWater: string;
  changeHeat: string;
  hideCommodityWater: boolean = this.environmentService.hideCommodityWater;
  showReviewSectionMessaging: boolean = this.environmentService.showReviewSectionMessaging;
  preventCanDeactivateCheck: boolean = false;

  technicalBaseDataForm: AbstractControl;
  powerConnectionForm: AbstractControl;
  gasConnectionForm: AbstractControl;
  heatingConnectionForm: AbstractControl;
  waterConnectionForm: AbstractControl;

  sectionIdentifier = SectionIdentifier;

  constructor(
    private jurisdictionService: JurisdictionService,
    private localStorageService: LocalStorageService,
    private technicianDataService: TechnicianDataService,
    private stateShare: NavigationStateShareService,
    private router: Router,
    private ngZone: NgZone,
    private commonService: CommonService,
    private communicationService: CommunicationService,
    private errorService: ErrorService,
    private environmentService: EnvironmentService
  ) {}

  canDeactivate(): boolean | Promise<boolean> {
    if (this.preventCanDeactivateCheck) {
      return true;
    }

    return (
      !this.technicalBaseDataForm?.dirty &&
      !this.powerConnectionForm?.dirty &&
      !this.gasConnectionForm?.dirty &&
      !this.heatingConnectionForm?.dirty &&
      !this.waterConnectionForm?.dirty
    );
  }

  get hideBaseDataForNewMainsPower(): boolean {
    return (
      this.currentFlowType === this.installationFlowType.MainsConnection &&
      this.changePower === '1' &&
      this.environmentService.hideBaseDataForNewMainsPower
    );
  }

  get showBaseDataSection(): boolean {
    const selectedChangeType = this.localStorageService.getItem('selected_change_type');
    return (
      this.currentFlowType !== this.installationFlowType.TemporaryConnection &&
      !this.hideBaseDataForNewMainsPower &&
      !(
        this.currentFlowType === this.installationFlowType.ChangeHouseConnection &&
        selectedChangeType === 'shutdown' &&
        this.environmentService.hideBaseDataForGridConnectionClosure
      ) &&
      this.baseData
    );
  }

  ngOnInit(): void {
    this.buildingTypeKey = AllDropdownKeys.BuildingType;
    this.changeSubtypeKey = AllDropdownKeys.ChangeSubtype;
    this.unitLabels = AllLabels.units;
    this.showFormElement = false;
    this.isFormSubmit = false;
    this.isLoading = false;
    this.disabledMediaOptions = false;
    this.formSubSections = {
      gas: false,
      heating: false,
      power: false,
      water: false,
    };
    this.showComponent = {
      gas: false,
      heating: false,
      power: false,
      water: false,
    };
    this.installationFlowType = InstallationFlowType;
    this.facilityTypes = this.localStorageService.getItem('selected_facilities') || [];
    this.currentFlowType = this.localStorageService.getItem('installation_flow');
    this.changeConnectionType =
      this.currentFlowType === InstallationFlowType.ChangeHouseConnection || this.currentFlowType === InstallationFlowType.ConversionToGas
        ? this.localStorageService.getItem('selected_change_type')
        : '';
    this.applicationId = this.localStorageService.getItem('application_id');
    this.specialDevicesCount = 0;
    this.totalSubscriptions = 0;
    this.changeFacilities = [];
    this.isPriceUndetermined = false;
    this.showDeterminePriceButton = this.environmentService.showDeterminePriceButton;
    this.showLabelAndUnit = this.environmentService.showLabelAndUnit;
    this.mediumVisibility = this.localStorageService.getItem('mediumVisibility');
    this.infoBubblesData = this.environmentService.getInfoBubblesData('technicianData') || null;

    this.technicianFormTypes =
      this.currentFlowType === InstallationFlowType.MainsConnection
        ? this.localStorageService.getItem('selected_building_type')
        : this.commonService.getTechnicianFlow(this.currentFlowType, this.changeConnectionType);

    this.changeConnectionTypes = this.commonService.getDropdownData(AllDropdownKeys.ChangeSubtype);

    if (this.technicianFormTypes.length) {
      this.setCurrentFormType();
      if (this.isReadOnly) {
        this.parseData();
      } else {
        this.getGridConnectionApplication();
      }

      this.handleFormData();
    }

    this.technicianDataService.registerForComponentFormUpdate.subscribe(data => {
      if (data.component === 'baseData') {
        this.technicalBaseDataForm = data.formData;
      } else if (data.component === 'powerConnection') {
        this.powerConnectionForm = data.formData;
        this.tempConnectionConfirmed.emit(data.formData.value?.tempPowerReportConfirmed ?? false);
      } else if (data.component === 'gasConnection') {
        this.gasConnectionForm = data.formData;
      } else if (data.component === 'heatingConnection') {
        this.heatingConnectionForm = data.formData;
      } else if (data.component === 'waterConnection') {
        this.waterConnectionForm = data.formData;
      }
    });
  }

  setCurrentFormType(): void {
    this.currentFormType = this.jurisdictionService.setCurrentFormType(this.currentFormType, this.technicianFormTypes);
  }

  getFlowTitle(): string {
    return this.commonService.parseTextFromCamelCase(this.currentFlowType);
  }

  prepareFormData(data?: GridConnectionData): void {
    if (data) {
      this.technicianDataService.gridConnectionApplicationData = data;
      this.technicianDataService.parseApplicationDataIntoForm(this.formSubSections);
    }
    this.technicianData = this.technicianDataService.getFormData();
    this.baseData = this.technicianData.baseData || {};
    this.gasConnectionData = this.technicianData.gasConnection || {};
    this.heatingConnectionData = this.technicianData.heatingConnection || {};
    this.powerConnectionData = this.technicianData.powerConnection || {};
    this.waterConnectionData = this.technicianData.waterConnection || {};
    this.showFormElement = true;
    this.applicationStatus = data.sk_status;
    this.evaluateComponentVisibility();
  }

  getGridConnectionApplication(): void {
    const applicationId = this.localStorageService.getItem('application_id');
    if (applicationId) {
      this.isLoading = true;
      this.technicianDataService.getGridConnectionApplication().subscribe(
        (response: GetGridConnectionObject) => {
          this.isLoading = false;
          this.errorService.throwErrorIfFailure(response, true);
          this.commonService.gridConnectionApplicationData = response.gridconapplication[0];
          this.parseData();
        },
        error => {
          this.isLoading = false;
          this.errorService.throwErrorIfFailure(error, true);
        }
      );
    }
  }

  handleFormData(): void {
    this.technicianDataService.registerForComponentFormUpdate.subscribe((data: ComponentFormData) => {
      if (data.component.length) {
        this.updateFormData(data.formData, data.component);
      }
    });
  }

  updateFormData(data: AbstractControl, formControl: string): void {
    this.technicianDataFG.controls[formControl] = data;
    this.technicianDataFG.updateValueAndValidity();
  }

  submitForm(): Promise<void> {
    return new Promise(async (resolve, reject) => {
      try {
        if (!this.isLoading) {
          this.preventCanDeactivateCheck = true;
          this.isFormSubmit = true;
          await this.updateInstallationData();
        }
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  }

  async determinePrice(): Promise<void> {
    this.isLoading = true;
    this.isFormSubmit = false;
    await this.updateInstallationData();
  }

  evaluateComponentVisibility(): void {
    this.showComponent.gas = this.gasConnectionData && this.changeFacilities.indexOf('gas') > -1;
    this.showComponent.heating = this.heatingConnectionData && this.changeFacilities.indexOf('heating') > -1;
    this.showComponent.power = this.powerConnectionData && this.changeFacilities.indexOf('power') > -1;
    this.showComponent.water = this.waterConnectionData && this.changeFacilities.indexOf('water') > -1;
  }

  evaluateFormValidity(): boolean {
    return (
      this.technicianDataFG.controls.baseData.invalid ||
      this.technicianDataFG.controls.gasConnection.invalid ||
      this.technicianDataFG.controls.heatingConnection.invalid ||
      this.technicianDataFG.controls.powerConnection.invalid ||
      this.technicianDataFG.controls.waterConnection.invalid ||
      (this.currentFlowType === this.installationFlowType.ChangeHouseConnection &&
        !(
          (this.mediumVisibility?.power && this.showComponent?.power) ||
          (this.mediumVisibility?.heating && this.showComponent?.heating) ||
          (this.mediumVisibility?.gas && this.showComponent?.gas) ||
          (this.mediumVisibility?.water && this.showComponent?.water)
        )) ||
      this.isLoading
    );
  }

  selectMedia(event: any): void {
    const element = (event.target && event.target.parentNode) || null;
    if (element) {
      const iconElement = element.children[0];
      const labelElement = element.children[1];
      if (!iconElement.classList.contains('active-icon')) {
        iconElement.classList.add('active-icon');
        labelElement.classList.add('active-icon-label');
        this.changeFacilities.push(event.target.id);
      } else {
        iconElement.classList.remove('active-icon');
        labelElement.classList.remove('active-icon-label');
        this.changeFacilities.splice(this.changeFacilities.indexOf(event.target.id), 1);
      }
    }
    this.changeFacilities = [...this.changeFacilities];
    this.evaluateComponentVisibility();
  }

  saveMedia(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const requestUrl = UrlSegments.GridConnectionApplication;
      const requestData: UpdateChangeHouseConnectionMedien = {
        sk_gridconapplication_type: this.commonService.getGridConnectionApplicationType(this.currentFlowType),
        sk_change_subtype: this.localStorageService.getItem('selected_change_type'),
        change_power: this.changeFacilities.indexOf('power') > -1 ? '1' : '0',
        change_gas: this.changeFacilities.indexOf('gas') > -1 ? '1' : '0',
        change_water: this.changeFacilities.indexOf('water') > -1 ? '1' : '0',
        change_heat: this.changeFacilities.indexOf('heating') > -1 ? '1' : '0',
      };

      this.isLoading = true;

      this.communicationService.updateData(`${requestUrl}${this.applicationId}`, requestData).subscribe(
        response => {
          this.isLoading = false;
          this.errorService.rejectIfError(response, reject);
          this.errorService.throwErrorIfFailure(response, true);
          const selectedFacilityTypes = [];

          FacilityFormType.forEach(item => {
            if (this.changeFacilities.indexOf(item.key) > -1) {
              selectedFacilityTypes.push(item);
            }
          });
          if (selectedFacilityTypes.length) {
            this.localStorageService.setItem('selected_facilities', selectedFacilityTypes);
          }
          this.updateApplication(selectedFacilityTypes);

          resolve();
        },
        error => {
          this.isLoading = false;
          reject(error);
          this.errorService.throwErrorIfFailure(error, true);
        }
      );
    });
  }

  private prepareForm(): void {
    this.technicianDataFG = new FormGroup({
      baseData: new FormGroup({}),
      gasConnection: new FormGroup({}),
      heatingConnection: new FormGroup({}),
      powerConnection: new FormGroup({}),
      waterConnection: new FormGroup({}),
      price: new FormControl({ value: '', disabled: true }),
    });
  }

  private checkForMediaTypes(): void {
    this.changePower =
      (this.commonService.gridConnectionApplicationData && this.commonService.gridConnectionApplicationData.change_power) || '0';
    this.changeGas =
      (this.commonService.gridConnectionApplicationData && this.commonService.gridConnectionApplicationData.change_gas) || '0';
    this.changeWater =
      (this.commonService.gridConnectionApplicationData && this.commonService.gridConnectionApplicationData.change_water) || '0';
    this.changeHeat =
      (this.commonService.gridConnectionApplicationData && this.commonService.gridConnectionApplicationData.change_heat) || '0';

    if (this.changePower === '1') {
      this.changeFacilities.push(FacilityFormType[0].key);
    }
    if (this.changeGas === '1') {
      this.changeFacilities.push(FacilityFormType[1].key);
    }
    if (this.changeWater === '1') {
      this.changeFacilities.push(FacilityFormType[2].key);
    }
    if (this.changeHeat === '1') {
      this.changeFacilities.push(FacilityFormType[3].key);
    }

    if (
      (this.facilityTypes.length &&
        this.currentFlowType !== InstallationFlowType.ConversionToGas &&
        this.currentFlowType !== InstallationFlowType.ChangeHouseConnection) ||
      this.isReadOnly
    ) {
      this.disabledMediaOptions = true;
    }
    this.changeFacilities.forEach(item => {
      this.formSubSections[item] = true;
    });

    this.changeFacilities = [...this.changeFacilities];
  }

  private getCalculatedPrice(): void {
    this.isLoading = true;
    this.technicianDataService.getCalculatedPrice().subscribe(
      (response: GetGridConnectionObject) => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(response, true);
        if (!isNaN(Number(response.gridconapplication[0].determinated_price))) {
          const priceValue = response.gridconapplication[0].determinated_price.replace('.', ',');
          this.technicianDataFG.controls.price.setValue(priceValue);
          this.isPriceUndetermined = false;
        } else {
          this.technicianDataFG.controls.price.reset();
          this.isPriceUndetermined = true;
        }
        this.commonService.gridConnectionApplicationData = response.gridconapplication[0];
        this.technicianData = null;
        this.prepareFormData(response.gridconapplication[0]);
      },
      error => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  private async updateInstallationData(): Promise<void> {
    try {
      switch (this.currentFlowType) {
        case InstallationFlowType.MainsConnection: {
          this.isLoading = true;
          await this.saveHousingBaseData();

          // save independent media in parallel to reduce overall wait time
          const saves: Promise<void>[] = [];
          if (this.changeFacilities.indexOf('gas') > -1 && this.changeConnectionType !== 'shutdown') {
            saves.push(this.saveHousingGasConnectionData());
          }
          if (this.changeFacilities.indexOf('power') > -1 && this.changeConnectionType !== 'shutdown') {
            saves.push(this.saveHousingPowerConnectionData());
          }
          if (this.changeFacilities.indexOf('water') > -1 && this.changeConnectionType !== 'shutdown') {
            saves.push(this.saveHousingWaterConnectionData());
          }
          if (this.changeFacilities.indexOf('heating') > -1 && this.changeConnectionType !== 'shutdown') {
            saves.push(this.saveHousingHeatingConnectionData());
          }

          if (saves.length) {
            await Promise.all(saves);
          }
          break;
        }
        case InstallationFlowType.TemporaryConnection: {
          this.isLoading = true;
          // power and water can be saved in parallel
          await Promise.all([this.saveHousingPowerConnectionData(), this.saveHousingWaterConnectionData()]);
          break;
        }
        case InstallationFlowType.ChangeHouseConnection:
        case InstallationFlowType.ConversionToGas: {
          this.isLoading = true;
          if (this.changeConnectionType === 'conversion_gas') {
            await this.saveHousingBaseData();
            await this.saveHousingGasConnectionData();
          } else if (this.changeConnectionType === 'conversion_heat') {
            await this.saveHousingBaseData();
            await this.saveHousingHeatingConnectionData();
          } else {
            await this.saveHousingBaseData();
            const saves: Promise<void>[] = [];
            if (this.changeFacilities.indexOf('gas') > -1) {
              saves.push(this.saveHousingGasConnectionData());
            }
            if (this.changeFacilities.indexOf('power') > -1) {
              saves.push(this.saveHousingPowerConnectionData());
            }
            if (this.changeFacilities.indexOf('water') > -1) {
              saves.push(this.saveHousingWaterConnectionData());
            }
            if (this.changeFacilities.indexOf('heating') > -1) {
              saves.push(this.saveHousingHeatingConnectionData());
            }

            if (saves.length) {
              await Promise.all(saves);
            }

            await this.saveMedia();
          }
          break;
        }
        case InstallationFlowType.FeedInSystem:
          this.isLoading = true;
          this.postSuccessfulSave();
      }

      // After all saves finished: either determine price or continue submit flow
      if (this.currentFlowType !== InstallationFlowType.FeedInSystem) {
        if (this.isFormSubmit) {
          this.postSuccessfulSave();
        } else {
          this.getCalculatedPrice();
        }
      }
    } catch (error) {
      this.isLoading = false;
      throw error;
    }
  }

  private postSuccessfulSave(): void {
    this.isLoading = false;
    this.technicianDataService.actionOnFormSubmit();
    const formData = this.technicianDataService.parseDataForSubmission(this.currentFormType.index, this.technicianDataFG);
    this.localStorageService.setItem(`${this.currentFormType.key}_technician_data`, formData);

    if (this.isDetailsView) {
      return;
    }

    if (this.currentFlowType === this.installationFlowType.MainsConnection) {
      if (this.currentFormType.index < this.technicianFormTypes[this.technicianFormTypes.length - 1].index) {
        this.showFormElement = false;
        setTimeout(() => {
          this.technicianData = null;
          this.commonService.gridConnectionApplicationData = null;
          this.setCurrentFormType();
          this.getGridConnectionApplication();
        }, 1000);
        window.scroll(0, 0);
      } else {
        this.ngZone.run(() => {
          this.preventCanDeactivateCheck = true;
          this.router.navigate(['/personal-data']);
          this.stateShare.setFormState(3);
        });
      }
    } else {
      this.ngZone.run(() => {
        this.preventCanDeactivateCheck = true;
        this.router.navigate(['/personal-data']);
        this.stateShare.setFormState(3);
      });
    }
  }

  private parseData() {
    const connectionData: GridConnectionData = { ...this.commonService.gridConnectionApplicationData };
    this.externalApplicationId = this.commonService.griConnectionApplicationId;
    this.checkForMediaTypes();
    this.technicianDataService.gridConnectionApplicationData = connectionData;
    this.technicianDataService.parseApplicationDataIntoForm(this.formSubSections);
    this.prepareForm();
    this.prepareFormData(connectionData);
  }

  //#region "methods for grid connection data form sections"
  private async saveHousingBaseData(): Promise<void> {
    const requestUrlSegment = UrlSegments.GridConnectionApplication;
    const formData = (this.technicianDataFG.get('baseData') as FormGroup).getRawValue();
    this.jurisdictionService.thirdPartyAffected = formData.swsThirdPartyAffected;
    this.jurisdictionService.isInterestInFibreOptics = formData.interestInFibreCheckbox || false;
    const requestData: GridConnectionData = this.technicianDataService.prepareBaseData(formData);

    try {
      const response: GetGridConnectionObject = (await this.communicationService
        .updateData(`${requestUrlSegment}${this.applicationId}`, requestData)
        .toPromise()) as GetGridConnectionObject;

      // throw or handle API-level failures
      this.errorService.throwErrorIfFailure(response, true);
    } catch (error) {
      this.isLoading = false;
      this.errorService.throwErrorIfFailure(error, true);
      throw error;
    }
  }

  private async saveHousingGasConnectionData(): Promise<void> {
    if (!(this.showComponent.gas || (this.changeConnectionType && this.changeConnectionType !== 'shutdown'))) {
      return;
    }

    const gridConnectionIds = this.commonService.allGridConnectionIds;
    const requestUrl = gridConnectionIds.gasConnectionId ? UrlSegments.UpdateGasGridConnection : UrlSegments.CreateGasGridConnection;
    const formData = (this.technicianDataFG.get('gasConnection') as FormGroup).getRawValue();
    const requestData = this.technicianDataService.prepareGasConnectionData(formData);

    try {
      if (gridConnectionIds.gasConnectionId) {
        const response: UpdateGasGridConnection = (await this.communicationService
          .updateData(`${requestUrl}${gridConnectionIds.gasConnectionId}`, requestData)
          .toPromise()) as UpdateGasGridConnection;
        this.errorService.throwErrorIfFailure(response, true);
      } else {
        requestData.application_id = this.applicationId;
        const response: CreateGasGridConnectionResponse = (await this.communicationService
          .postData(`${requestUrl}`, requestData)
          .toPromise()) as CreateGasGridConnectionResponse;
        this.errorService.throwErrorIfFailure(response, true);
        this.commonService.allGridConnectionIds.gasConnectionId = response.gasgridcon[0].gridcon_id;
      }
    } catch (error) {
      this.isLoading = false;
      this.errorService.throwErrorIfFailure(error, true);
      throw error;
    }
  }

  private async saveHousingHeatingConnectionData(): Promise<void> {
    if (!(this.showComponent.heating || (this.changeConnectionType && this.changeConnectionType !== 'shutdown'))) {
      return;
    }

    const gridConnectionIds = this.commonService.allGridConnectionIds;
    const requestUrl = gridConnectionIds.heatingConnectionId
      ? UrlSegments.UpdateHeatingGridConnection
      : UrlSegments.CreateHeatingGridConnection;
    const formData = (this.technicianDataFG.get('heatingConnection') as FormGroup).getRawValue();
    const requestData = this.technicianDataService.prepareHeatingConnectionData(formData);

    try {
      if (gridConnectionIds.heatingConnectionId) {
        const response: UpdateHeatingGridConnection = (await this.communicationService
          .updateData(`${requestUrl}${gridConnectionIds.heatingConnectionId}`, requestData)
          .toPromise()) as UpdateHeatingGridConnection;
        this.errorService.throwErrorIfFailure(response, true);
      } else {
        requestData.application_id = this.applicationId;
        const response: CreateHeatingGridConnectionResponse = (await this.communicationService
          .postData(`${requestUrl}`, requestData)
          .toPromise()) as CreateHeatingGridConnectionResponse;
        this.errorService.throwErrorIfFailure(response, true);
        this.commonService.allGridConnectionIds.heatingConnectionId = response.districtheatgridcon[0].gridcon_id;
      }
    } catch (error) {
      this.isLoading = false;
      this.errorService.throwErrorIfFailure(error, true);
      throw error;
    }
  }

  private async saveHousingPowerConnectionData(): Promise<void> {
    if (!(this.showComponent.power || this.changeConnectionType)) {
      return;
    }

    const gridConnectionIds = this.commonService.allGridConnectionIds;
    const requestUrl = gridConnectionIds.powerConnectionId ? UrlSegments.UpdatePowerGridConnection : UrlSegments.CreatePowerGridConnection;
    const formData =
      this.currentFlowType === this.installationFlowType.ChangeHouseConnection && this.changeConnectionType === 'shutdown'
        ? this.technicianDataFG.value.baseData.siteElectricityCheckbox
          ? (this.technicianDataFG.get('baseData') as FormGroup).getRawValue()
          : null
        : (this.technicianDataFG.get('powerConnection') as FormGroup).getRawValue();

    if (!formData) {
      return;
    }

    this.localStorageService.setItem('bdewId', formData.technicianBdewId);
    const requestData = this.technicianDataService.preparePowerConnectionData(formData);

    try {
      if (gridConnectionIds.powerConnectionId) {
        const response: UpdatePowerGridConnection = (await this.communicationService
          .updateData(`${requestUrl}${gridConnectionIds.powerConnectionId}`, requestData)
          .toPromise()) as UpdatePowerGridConnection;
        this.errorService.throwErrorIfFailure(response, true);
        await this.handleSpecialConsumerData(response);
      } else {
        requestData.application_id = this.applicationId;
        const response: CreatePowerGridConnectionResponse = (await this.communicationService
          .postData(`${requestUrl}`, requestData)
          .toPromise()) as CreatePowerGridConnectionResponse;
        this.errorService.throwErrorIfFailure(response, true);
        this.commonService.allGridConnectionIds.powerConnectionId = response.powergridcon[0].gridcon_id;
        await this.handleSpecialConsumerData(response);
      }
    } catch (error) {
      this.isLoading = false;
      this.errorService.throwErrorIfFailure(error, true);
      throw error;
    }
  }

  getSpecialConsumerIdentities(): SpecialConsumerIdentity[] {
    const specialDevices = this.technicianDataFG?.get('powerConnection.specialDevice') as FormArray;

    return (specialDevices?.controls ?? [])
      .filter(device => !!device.get('tempKey')?.value)
      .map(device => ({
        tempKey: device.get('tempKey').value,
        consumerId: device.get('id')?.value ? String(device.get('id').value) : null,
      }));
  }

  private async handleSpecialConsumerData(powerGridResponse: CreatePowerGridConnectionResponse | UpdatePowerGridConnection): Promise<void> {
    const formGroup =
      this.currentFlowType === this.installationFlowType.ChangeHouseConnection && this.changeConnectionType === 'shutdown'
        ? (this.technicianDataFG.get('baseData') as FormGroup)
        : (this.technicianDataFG.get('powerConnection') as FormGroup);
    const formData = formGroup.getRawValue();
    const specialDeviceControls = formGroup.get('specialDevice') as FormArray;

    if (formData.specialDevice && formData.specialDevice.length) {
      const promises = formData.specialDevice.map((item, index) =>
        this.saveSpecialConsumerData(item, powerGridResponse, specialDeviceControls?.at(index))
      );
      await Promise.all(promises);
    }
  }

  private async saveSpecialConsumerData(
    data: any,
    powerGridResponse: CreatePowerGridConnectionResponse | UpdatePowerGridConnection,
    deviceControl?: AbstractControl
  ): Promise<void> {
    const requestUrl = data.id ? UrlSegments.UpdatePowerGridSpecialConsumer : UrlSegments.CreatePowerGridSpecialConsumer;
    const requestData = this.technicianDataService.prepareSpecialConsumerData(data);

    try {
      if (data.id) {
        const response: UpdatePowerSpecialConsumer = (await this.communicationService
          .updateData(`${requestUrl}${data.id}`, requestData)
          .toPromise()) as UpdatePowerSpecialConsumer;
        this.errorService.throwErrorIfFailure(response, true);
      } else {
        requestData.gridcon_id = powerGridResponse.powergridcon[0].gridcon_id;
        const response: CreatePowerSpecialConsumerResponse = (await this.communicationService
          .postData(`${requestUrl}`, requestData)
          .toPromise()) as CreatePowerSpecialConsumerResponse;
        this.errorService.throwErrorIfFailure(response, true);

        const newId = response?.specialconsumer?.[0]?.specialconsumer_id;
        if (newId) {
          deviceControl?.get('id')?.setValue(String(newId));
        }
      }
    } catch (error) {
      this.isLoading = false;
      this.errorService.throwErrorIfFailure(error, true);
      throw error;
    }
  }

  private async saveHousingWaterConnectionData(): Promise<void> {
    if (!(this.showComponent.water || this.changeConnectionType)) {
      return;
    }

    const gridConnectionIds = this.commonService.allGridConnectionIds;
    const requestUrl = gridConnectionIds.waterConnectionId ? UrlSegments.UpdateWaterGridConnection : UrlSegments.CreateWaterGridConnection;
    const formData =
      this.currentFlowType === this.installationFlowType.ChangeHouseConnection && this.changeConnectionType === 'shutdown'
        ? this.technicianDataFG.value.baseData.siteWaterCheckbox
          ? (this.technicianDataFG.get('baseData') as FormGroup).getRawValue()
          : null
        : (this.technicianDataFG.get('waterConnection') as FormGroup).getRawValue();

    if (!formData) {
      return;
    }

    const requestData = this.technicianDataService.prepareWaterConnectionData(formData);

    try {
      if (gridConnectionIds.waterConnectionId) {
        const response: UpdateWaterGridConnection = (await this.communicationService
          .updateData(`${requestUrl}${gridConnectionIds.waterConnectionId}`, requestData)
          .toPromise()) as UpdateWaterGridConnection;
        this.errorService.rejectIfError(response, null);
        this.errorService.throwErrorIfFailure(response, true);
      } else {
        requestData.application_id = this.applicationId;
        const response: CreateWaterGridConnectionResponse = (await this.communicationService
          .postData(`${requestUrl}`, requestData)
          .toPromise()) as CreateWaterGridConnectionResponse;
        this.errorService.rejectIfError(response, null);
        this.errorService.throwErrorIfFailure(response, true);
        this.commonService.allGridConnectionIds.waterConnectionId = response.watergridcon[0].gridcon_id;
      }
    } catch (error) {
      this.isLoading = false;
      this.errorService.throwErrorIfFailure(error, true);
      throw error;
    }
  }

  private updateApplication(selectedFacilityTypes: FormTypesData[]): void {
    let subscriptionCount = 0;
    const applicationData = this.commonService.gridConnectionApplicationData;
    FacilityFormType.forEach(item => {
      const selectedFacility = selectedFacilityTypes.filter(type => type.key === item.key);
      let installationId = '';
      let urlSegment = '';

      switch (item.key) {
        case 'power':
          if (!selectedFacility.length) {
            installationId = applicationData.powergridcon && applicationData.powergridcon.gridcon_id;
            urlSegment = `${UrlSegments.UpdatePowerGridConnection}${installationId}`;
          }
          break;
        case 'gas':
          if (!selectedFacility.length) {
            installationId = applicationData.gasgridcon && applicationData.gasgridcon.gridcon_id;
            urlSegment = `${UrlSegments.UpdateGasGridConnection}${installationId}`;
          }
          break;
        case 'water':
          if (!selectedFacility.length) {
            installationId = applicationData.watergridcon && applicationData.watergridcon.gridcon_id;
            urlSegment = `${UrlSegments.UpdateWaterGridConnection}${installationId}`;
          }
          break;
        case 'heating':
          if (!selectedFacility.length) {
            installationId = applicationData.districtheatgridcon && applicationData.districtheatgridcon.gridcon_id;
            urlSegment = `${UrlSegments.UpdateHeatingGridConnection}${installationId}`;
          }
          break;
      }
      if (installationId) {
        subscriptionCount += 1;
        this.isLoading = true;
        this.commonService.deleteMediaObjectsFromApplication(urlSegment).subscribe(
          applicationResponse => {
            this.isLoading = false;
            subscriptionCount -= 1;
            this.errorService.throwErrorIfFailure(applicationResponse, true);

            if (subscriptionCount === 0) {
              this.isLoading = true;

              this.commonService.getExistingGridConnectionApplication(this.currentFlowType).subscribe(
                (response: GetGridConnectionObject) => {
                  this.isLoading = false;
                  this.errorService.throwErrorIfFailure(response, true);
                  if (response && response.gridconapplication && response.gridconapplication.length) {
                    this.commonService.gridConnectionApplicationData = response.gridconapplication[0];
                  }
                },
                error => {
                  this.isLoading = false;
                  this.errorService.throwErrorIfFailure(error, true);
                }
              );
            }
          },
          error => {
            this.isLoading = false;
            this.errorService.throwErrorIfFailure(error, true);
          }
        );
      }
    });
  }

  //#endregion
}
